import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, ChevronRight, ChevronDown, Plus, Trash2, Save, Undo2, Loader2, Paperclip, X, Download, Upload, FileText, ArchiveRestore, History, Pencil, Check,
} from "lucide-react";
import { fmtCurrency, fmtDate } from "../lib/formatters";
import { useAuth, canEdit } from "../contexts/AuthContext";
import AddElementModal from "../components/AddElementModal";
import ImportElementsModal from "../components/ImportElementsModal";
import AttachmentsModal from "../components/AttachmentsModal";
import AttachmentHoverPreview, { invalidateAttachmentHoverCache } from "../components/AttachmentHoverPreview";
import DeletedElementsDrawer from "../components/DeletedElementsDrawer";
import ElementCostOverTimeModal from "../components/ElementCostOverTimeModal";
import ElementHistoryDrawer from "../components/ElementHistoryDrawer";
import ConfirmDialog from "../components/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Slice E1 — Elements CRUD (Dave 2026-02-17)
//
// UX rules chosen with the user:
//   • Inline editable cells (Tab moves between fields) — no per-row modal
//   • Global batch Undo / Save: all edits pool into a single dirty map, one
//     atomic PATCH commits them all.
//   • + Element opens a modal (fresh POST, immediate save & recompute)
//   • Delete = soft (is_active=false); Analyst+ can trigger it. Purge is
//     Administrator-only and lives behind a future "Show deactivated" toggle.
//   • Viewer: everything read-only, no controls shown.
//   • On Save: server returns the fresh bundle → App runs calc again so the
//     Dashboard's anchor badges + projections reflect the new elements.
// ---------------------------------------------------------------------------

// Fields the user can edit inline. Order matches column layout below.
const EDITABLE_FIELDS = [
  "name", "category", "quantity", "uom",
  "current_cost", "useful_life_when_new", "remaining_useful_life",
  // 2026-02-28 (Dave) — inline "Include" checkbox before Notes. Toggling
  // it flips `PlanYearLineItem.included_in_plan` on save; when false the
  // engine skips the element entirely (see engine.py:144).
  "included_in_plan",
];

// Normalize a cell value so we can compare pristine vs. edit reliably.
function normalize(field, v) {
  if (field === "included_in_plan") {
    // Boolean coercion — the backend expects a real bool, not a string.
    if (v === true || v === "true") return true;
    if (v === false || v === "false") return false;
    // Pristine element from server: null/undefined defaults to true.
    return v === null || v === undefined ? true : Boolean(v);
  }
  if (v === null || v === undefined) return "";
  if (["quantity", "current_cost"].includes(field)) return parseFloat(v) || 0;
  if (["useful_life_when_new", "remaining_useful_life"].includes(field)) {
    return parseInt(v, 10) || 0;
  }
  return String(v);
}

function SortableTh({ top, bottom, sortKey, align = "left", width, padLeft, padRight, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{
        cursor: "pointer",
        userSelect: "none",
        position: "sticky",
        top: 0,
        // 2026-02-16 (Dave) — dark navy header with white text to match
        // the Contributions tab header treatment. Housekeeping backlog:
        // Dave to define a canonical table-header standard we can share
        // across screens.
        background: "#1C4E80",
        zIndex: 1,
        width,
        verticalAlign: "bottom",
        lineHeight: 1.1,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: padLeft != null ? padLeft : undefined,
        paddingRight: padRight != null ? padRight : undefined,
        color: "#fff",
        fontWeight: 700,
        // Inline text-align beats the global `.u-table th { text-align: left }`
        // specificity (Dave 2026-02-09 · elements columns not right-justified).
        textAlign: align,
      }}
      data-testid={`th-${sortKey}`}
    >
      {top && <div>{top}</div>}
      <div>
        {bottom}
        {active && <span className="ml-1">{sort.dir === "asc" ? "▲" : "▼"}</span>}
      </div>
    </th>
  );
}

// Plain non-sortable header cell — matches SortableTh visual style for
// consistency. Used for the ID column (no natural sort key) and for the
// static right-side "Delete" column.
function PlainTh({ top, bottom, align = "left", width, padLeft, children }) {
  return (
    <th
      style={{
        position: "sticky",
        top: 0,
        // 2026-02-16 (Dave) — dark navy header with white text to match
        // the Contributions tab header treatment (see SortableTh above).
        background: "#1C4E80",
        zIndex: 1,
        width,
        verticalAlign: "bottom",
        lineHeight: 1.1,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: padLeft != null ? padLeft : undefined,
        color: "#fff",
        fontWeight: 700,
        textAlign: align,
      }}
    >
      {children || (
        <>
          {top && <div>{top}</div>}
          <div>{bottom}</div>
        </>
      )}
    </th>
  );
}

// Inline cell that switches between display and edit modes.
// Change handler bubbles up to the parent's `pendingEdits` state.
function EditableCell({
  el, field, type = "text", align = "left", step, onEdit, isDirty, readOnly,
  format,
}) {
  const raw = el[field];
  const editedValue = isDirty !== undefined ? isDirty : raw;
  const displayed = format ? format(editedValue) : editedValue;

  if (readOnly) {
    return (
      <td
        className={align === "right" ? "num" : ""}
        style={align === "right" ? undefined : { color: "var(--u-slate)" }}
      >
        {displayed ?? "—"}
      </td>
    );
  }

  return (
    <td className={align === "right" ? "num" : ""}>
      <input
        type={type}
        step={step}
        value={editedValue ?? ""}
        onChange={(e) => {
          const v = type === "number" ? e.target.value : e.target.value;
          onEdit(el.element_id, field, v);
        }}
        className={`u-cell-input ${isDirty !== undefined ? "is-dirty" : ""}`}
        style={align === "right" ? { textAlign: "right", fontFamily: "IBM Plex Mono, monospace" } : undefined}
        data-testid={`cell-${el.element_id}-${field}`}
      />
    </td>
  );
}

// Parse the ID + description out of the composite `name` field. Format the
// ID as nnn.nn per Dave 2026-02-17 (e.g. "358" → "358.00", "359.02" stays).
const NAME_ID_RE = /^(\d+(?:\.\d+)?)\s+(.*)$/;
function splitNameId(name) {
  const m = (name || "").match(NAME_ID_RE);
  if (!m) return { id: "", desc: name || "" };
  return { id: m[1], desc: m[2] };
}
function fmtId(id) {
  if (!id) return "";
  // Slice 5f — element_display_id is stored as a canonical NNN.NNN string
  // (e.g. "100.001"). Preserve it verbatim if it matches that shape;
  // otherwise fall back to the legacy numeric formatter (`358.02`).
  if (typeof id === "string" && /^\d{3}\.\d{2}\.\d{3}$/.test(id)) return id;
  if (typeof id === "string" && /^\d+\.\d+$/.test(id) && id.split(".")[1].length >= 3) {
    return id;
  }
  const n = parseFloat(id);
  return Number.isFinite(n) ? n.toFixed(3) : id;
}

// Slice E3 — live-preview header stat.
// Slice E5b (2026-02-09) — reflowed to a single-line layout so the header
// row (search + toggles + stats) all share the same height. Each stat is
// its own navy-bordered pill (matching the Scenario History button style)
// with a tight 3-px radius. Label sits left, value right, both on one line.
// 2026-03-01 (Dave) — Attachment count badge with hover-preview popover.
// The popover UI now lives in the shared AttachmentHoverPreview
// component; this thin wrapper just handles the fixed-width count slot
// so the trash icon to its right never shifts.
function AttachmentCountBadge({ elementId, count }) {
  return (
    <AttachmentHoverPreview
      parent={{ type: "element", id: elementId }}
      count={count}
      side="left"
      wrapperClass="relative inline-block text-center"
      wrapperStyle={{ width: 18 }}
      testid={`attach-count-${elementId}`}
    >
      <span className="text-[9px] font-semibold text-navy">
        {count > 0 ? `(${count})` : ""}
      </span>
    </AttachmentHoverPreview>
  );
}


// Currency cell — displays formatted "$20,000" when idle, reveals the raw
// number on focus for easy editing. Used for Cost / UoM.
function CurrencyCell({ el, field, value, isDirty, onEdit, readOnly }) {
  const [focused, setFocused] = React.useState(false);
  const raw = isDirty !== undefined ? isDirty : value;
  if (readOnly) {
    return <td className="num">{fmtCurrency(raw)}</td>;
  }
  return (
    <td className="num">
      {focused ? (
        <input
          autoFocus
          type="number"
          step="0.01"
          value={raw ?? 0}
          onChange={(e) => onEdit(el.element_id, field, e.target.value)}
          onBlur={() => setFocused(false)}
          className={`u-cell-input ${isDirty !== undefined ? "is-dirty" : ""}`}
          style={{ textAlign: "right", fontFamily: "IBM Plex Mono, monospace" }}
          data-testid={`cell-${el.element_id}-${field}`}
        />
      ) : (
        <button
          type="button"
          onClick={() => setFocused(true)}
          className={`u-cell-input ${isDirty !== undefined ? "is-dirty" : ""}`}
          style={{
            textAlign: "right",
            fontFamily: "IBM Plex Mono, monospace",
            cursor: "text",
            width: "100%",
            display: "block",
          }}
          data-testid={`cell-${el.element_id}-${field}`}
        >
          {fmtCurrency(raw)}
        </button>
      )}
    </td>
  );
}


// Inline category rename: Enter saves, Esc cancels.
function CategoryRenameInput({ current, saving, validate, onSubmit, onCancel }) {
  const [value, setValue] = useState(current === "Uncategorized" ? "" : current);
  const err = validate(value);
  const submit = () => { if (!err && !saving) onSubmit(value); };
  return (
    <span className="inline-flex items-center gap-1.5 normal-case tracking-normal" onClick={(ev) => ev.stopPropagation()}>
      <input
        autoFocus
        value={value}
        disabled={saving}
        onChange={(ev) => setValue(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter") submit();
          if (ev.key === "Escape") onCancel();
        }}
        className="border border-sky rounded-[3px] px-1.5 py-0.5 text-[12px] text-navy font-semibold w-56 bg-white"
        data-testid="group-rename-input"
      />
      <button type="button" onClick={submit} disabled={!!err || saving} data-testid="group-rename-save"
        className="text-sky hover:text-navy disabled:opacity-40 transition-colors" title="Save" aria-label="Save">
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} />}
      </button>
      <button type="button" onClick={onCancel} disabled={saving} data-testid="group-rename-cancel"
        className="text-slate hover:text-coral transition-colors" title="Cancel" aria-label="Cancel">
        <X size={13} />
      </button>
      {err && value.trim() !== current && !(current === "Uncategorized" && value === "") && (
        <span className="text-[10.5px] text-coral font-normal" data-testid="group-rename-error">{err}</span>
      )}
    </span>
  );
}

// Category-level Include box: checked / dash (partial) / unchecked.
function GroupIncludeBox({ items, isIncluded, disabled, onChange, testId }) {
  const ref = useRef(null);
  const n = items.filter(isIncluded).length;
  const all = items.length > 0 && n === items.length;
  const some = n > 0 && !all;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some;
  }, [some]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={all}
      disabled={disabled}
      onChange={() => onChange(!all)}
      style={{ accentColor: "#8a94a6" }}
      title={all ? "Exclude all in this category" : "Include all in this category"}
      data-testid={testId}
      data-state={all ? "all" : some ? "some" : "none"}
    />
  );
}


function ElementRow({
  e, studyYear, grouped, pendingEdits, onEdit, onDelete, onOpenAttachments, onOpenHistory, readOnly, deleting,
  canDeleteRow,        // Step 2 (2026-02-25) — per-row predicate
  cannotDeleteReason,  // tooltip when the trash icon is hidden
}) {
  const pending = pendingEdits[e.element_id] || {};
  // 2026-09-27 (Dave) — coerce the raw <input value=""> string to an
  // integer BEFORE the addition. Without parseInt the `+` operator
  // performs JS string concatenation on the pending value ("2027" + "3"
  // = "20273") which surfaced as 5-digit replacement years on
  // Lake Villas while a user was mid-edit on the RUL cell.
  const rulPending = pending.remaining_useful_life;
  const effRul = rulPending !== undefined
    ? (parseInt(rulPending, 10) || 0)
    : (e.remaining_useful_life ?? 0);
  const nextYear = studyYear + effRul;
  const soon = effRul <= 5;

  // Extended cost is the saved, server-computed value (updates after Save).
  const effExt = e.extended_cost || 0;

  // Split name → ID + description. Prefer pending.name if the user edited it
  // (via the Description column below).
  //
  // Slice 5f (2026-02-24) — prefer the new dedicated `element_display_id`
  // column when the server provides it; only fall back to the legacy
  // "prefix embedded in the name" convention for pre-migration rows.
  const parsed = splitNameId(pending.name ?? e.name);
  const rawId = e.element_display_id || parsed.id;
  const id = pending.element_display_id ?? rawId;
  const desc = parsed.desc;
  const descDirty = pending.name !== undefined;
  const idDirty = pending.element_display_id !== undefined;

  // 2026-02-27 (Dave) — Element ID is now inline-editable. Prefix
  // (NNN) is fixed to the association's tenant_display_id and rendered
  // as a static span; only the two-digit suffix (NN) is user-editable.
  // Backend `_validate_display_id_prefix` accepts a bare "NN" and
  // rehydrates to "NNN.NN", so we send just the suffix.
  //
  // 2026-02-28 (Dave) — Prefix must be sourced from the PRISTINE element,
  // never from `pending`. During inline editing the pending value is a
  // bare "NN" (no dot), which would collapse the prefix to empty and make
  // the visible ID look zeroed out. Compute prefix from `rawId`, and only
  // compute suffix from `id` (pending || rawId).
  // 2026-10-16 (Dave) — ID = AAA.GG.EEE: AAA locked; GG (group) and EEE
  // (element) editable. Pending edit holds the bare "GG.EEE".
  const rawParts = (typeof rawId === "string" ? rawId : "").split(".");
  const idPrefix = rawParts.length === 3 ? rawParts[0] : "";
  const curBare = pending.element_display_id !== undefined
    ? String(pending.element_display_id)
    : (rawParts.length === 3 ? `${rawParts[1]}.${rawParts[2]}` : "");
  const [idGroup = "", idElem = ""] = curBare.split(".");
  const onIdPart = (part, val, pad) => {
    let clean = String(val).replace(/\D/g, "").slice(0, part === "g" ? 2 : 3);
    if (pad && clean) clean = clean.padStart(part === "g" ? 2 : 3, "0");
    const g = part === "g" ? clean : idGroup;
    const el = part === "e" ? clean : idElem;
    onEdit(e.element_id, "element_display_id", `${g}.${el}`);
  };

  // Description edits reassemble the full `name` so the server continues to
  // receive one consistent field.
  const onDescEdit = (val) => {
    // Preserve the original ID prefix even if the user clears the desc.
    const originalId = splitNameId(e.name).id;
    onEdit(e.element_id, "name", originalId ? `${originalId} ${val}` : val);
  };

  // 2026-02-28 (Dave) — Excluded row styling: dim + italic the whole row
  // when Include is unchecked (or a pending edit will uncheck it on Save),
  // so the eye immediately catches which elements the engine is skipping.
  const includedEff = pending.included_in_plan !== undefined
    ? Boolean(pending.included_in_plan)
    : e.included_in_plan !== false;

  return (
    <tr
      data-testid={`element-row-${e.element_id}`}
      data-included={includedEff ? "yes" : "no"}
      style={
        includedEff
          ? undefined
          : { opacity: 0.55, fontStyle: "italic", background: "#f8f9fb" }
      }
    >
      {/* 1. ID — prefix locked to the association's tenant_display_id;
             suffix (2 digits) is user-editable for sort ordering.
             Editing bypasses the "name" column so both can be dirty
             independently. The Save button sends just the suffix; the
             backend `_validate_display_id_prefix` rehydrates to
             "NNN.NN". */}
      <td
        className="font-mono text-ink"
        style={{ whiteSpace: "nowrap", textAlign: "right", padding: "0 6px 0 18px" }}
        data-testid={`id-${e.element_id}`}
      >
        {readOnly || !idPrefix ? (
          fmtId(id) || "—"
        ) : (
          <span
            className="inline-flex items-center justify-end"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
          >
            <span className="text-slate select-none">{idPrefix}.</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={2}
              value={idGroup}
              onChange={(ev) => onIdPart("g", ev.target.value)}
              onBlur={(ev) => onIdPart("g", ev.target.value, true)}
              className={`u-cell-input ${idDirty ? "is-dirty" : ""}`}
              style={{ width: 20, textAlign: "left", padding: "2px 1px" }}
              data-testid={`id-group-input-${e.element_id}`}
              aria-label="Element ID group digits"
              title="Group digits (00, 05, 10 …)"
            />
            <span className="text-slate select-none">.</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={3}
              value={idElem}
              onChange={(ev) => onIdPart("e", ev.target.value)}
              onBlur={(ev) => onIdPart("e", ev.target.value, true)}
              className={`u-cell-input ${idDirty ? "is-dirty" : ""}`}
              style={{ width: 30, textAlign: "left", padding: "2px 1px" }}
              data-testid={`id-input-${e.element_id}`}
              aria-label="Element ID element digits"
              title="Element digits within the group (001, 002 …)"
            />
          </span>
        )}
      </td>

      {/* 2. Description (was Name) — editable text */}
      <td>
        {readOnly ? (
          <span className="text-slate">{desc || "—"}</span>
        ) : (
          <input
            value={desc}
            onChange={(ev) => onDescEdit(ev.target.value)}
            className={`u-cell-input ${descDirty ? "is-dirty" : ""}`}
            data-testid={`cell-${e.element_id}-name`}
          />
        )}
      </td>

      {!grouped && (
        <EditableCell
          el={e}
          field="category"
          onEdit={onEdit}
          isDirty={pending.category}
          readOnly={readOnly}
        />
      )}

      {/* 3. Useful Life (was ULWN) */}
      <EditableCell
        el={e}
        field="useful_life_when_new"
        type="number"
        align="right"
        onEdit={onEdit}
        isDirty={pending.useful_life_when_new}
        readOnly={readOnly}
      />

      {/* 4. Remaining Life (was RUL) — plain number per Dave 2026-02-17 */}
      <td className="num">
        {readOnly ? (
          pending.remaining_useful_life ?? e.remaining_useful_life
        ) : (
          <input
            type="number"
            value={pending.remaining_useful_life ?? e.remaining_useful_life ?? ""}
            onChange={(ev) => onEdit(e.element_id, "remaining_useful_life", ev.target.value)}
            className={`u-cell-input ${pending.remaining_useful_life !== undefined ? "is-dirty" : ""}`}
            style={{ textAlign: "right", fontFamily: "IBM Plex Mono, monospace" }}
            data-testid={`cell-${e.element_id}-remaining_useful_life`}
          />
        )}
      </td>

      {/* 5. Quantity */}
      <EditableCell
        el={e}
        field="quantity"
        type="number"
        step="0.01"
        align="right"
        onEdit={onEdit}
        isDirty={pending.quantity}
        readOnly={readOnly}
      />

      {/* 6. UOM */}
      <EditableCell
        el={e}
        field="uom"
        align="right"
        onEdit={onEdit}
        isDirty={pending.uom}
        readOnly={readOnly}
      />

      {/* 7. Cost / UOM — currency-formatted display, click to edit as raw */}
      <CurrencyCell
        el={e}
        field="current_cost"
        value={e.current_cost}
        isDirty={pending.current_cost}
        onEdit={onEdit}
        readOnly={readOnly}
      />

      {/* 8. Extended Cost — derived, always read-only, currency.
          Slice E5f (2026-02-09) — bold removed per Dave; matches the
          regular weight of the other numeric columns. */}
      <td className="num" data-testid={`ext-${e.element_id}`}>
        {fmtCurrency(effExt)}
      </td>

      {/* 9. Next Replacement */}
      <td className="num text-slate">{nextYear}</td>

      {/* 10. Last Inspection */}
      <td className="num text-slate">{fmtDate(e.last_inspection_date)}</td>

      {/* 11. Placed In Service */}
      <td className="num text-slate">{fmtDate(e.date_placed_in_service)}</td>

      {/* 11.5 Include (2026-02-28 Dave) — inline checkbox. Checked
          means the engine takes this element into account; unchecked
          skips it in every projection until re-enabled. Uses the
          same pending-edit / batch-save pipeline as the numeric
          cells, and mirrors the pristine `included_in_plan` on the
          row (defaults to true when missing from legacy payloads). */}
      <td className="text-center">
        {readOnly ? (
          <input
            type="checkbox"
            disabled
            checked={e.included_in_plan !== false}
            style={{ accentColor: "#8a94a6" }}
            data-testid={`include-${e.element_id}`}
          />
        ) : (
          <input
            type="checkbox"
            checked={
              pending.included_in_plan !== undefined
                ? Boolean(pending.included_in_plan)
                : e.included_in_plan !== false
            }
            onChange={(ev) => onEdit(e.element_id, "included_in_plan", ev.target.checked)}
            className={pending.included_in_plan !== undefined ? "u-cell-input is-dirty" : ""}
            style={{ accentColor: "#8a94a6" }}
            data-testid={`include-${e.element_id}`}
          />
        )}
      </td>

      {/* 12. Attachments (formerly Notes, repurposed 2026-03-01 Dave).
          Holds the paperclip + count badge + row soft-delete trash. Left-
          aligned so the cluster hugs the column's left edge; generous gap
          between the count badge and the trash so the two actions read
          as distinct affordances. */}
      <td className="whitespace-nowrap" style={{ paddingLeft: 6, paddingRight: 4 }}>
        <div className="flex items-center gap-1 pl-0">
          <button
            onClick={() => onOpenAttachments(e)}
            className="text-slate hover:text-navy inline-flex items-center"
            title="Attachments"
            data-testid={`attach-${e.element_id}`}
          >
            <Paperclip size={12} />
          </button>
          {/* Count badge: hover shows a thumbnails popover of attached
              files (fetched lazily). Fixed width keeps the trash icon
              still even when count===0. */}
          <AttachmentCountBadge
            elementId={e.element_id}
            count={e.attachment_count || 0}
          />
          {/* 2026-03-09 (Dave · Slice 4) — Element History drawer trigger.
              ⟳ icon opens a right-side slide-out listing per-field baseline
              edits. Admin + Analyst only (writers); hidden for view-only. */}
          {!readOnly && onOpenHistory && (
            <button
              onClick={() => onOpenHistory(e)}
              className="text-slate hover:text-navy inline-flex items-center ml-2"
              title="Element history"
              data-testid={`history-${e.element_id}`}
            >
              <History size={12} />
            </button>
          )}
          {!readOnly && (
            canDeleteRow ? (
              <button
                onClick={() => onDelete(e)}
                disabled={deleting}
                className="text-slate hover:text-coral disabled:opacity-40 inline-flex items-center ml-3"
                title={`Soft-delete ${e.name}`}
                data-testid={`delete-${e.element_id}`}
              >
                {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              </button>
            ) : (
              <span
                className="text-slate text-[10px] ml-3"
                title={cannotDeleteReason || "You cannot delete this element"}
                data-testid={`delete-locked-${e.element_id}`}
              >
                —
              </span>
            )
          )}
        </div>
      </td>
    </tr>
  );
}

// Screen 3 — Elements inventory. Search / group / sort still work; on top of
// that, writers now have inline edit + Undo/Save + Add + Delete.
// Live header figure; turns coral while element edits are pending.
function LiveStat({ label, value, dirty, loading, testid }) {
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap font-mono tabular-nums" data-testid={testid}>
      <span className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy">{label}:</span>
      <span className={`text-[12px] font-semibold ${dirty ? "text-coral" : "text-ink"}`}>
        {value == null ? "—" : `$${Math.round(value).toLocaleString()}`}
      </span>
      {loading && <Loader2 size={11} className="animate-spin text-slate" data-testid="live-stats-loading" />}
    </div>
  );
}

export default function ElementsScreen({
  association, onBundleUpdated, currentVariables,
  activeScenarioId, calc,
  onPreviewLoadingChange,
}) {
  const { user } = useAuth();
  const writer = canEdit(user);
  // 2026-10-13 (Dave) — Analysts and Administrators may delete any element.
  const canDeleteElement = () => writer;
  const deleteLockReason = () => (writer ? "" : "View-only users cannot delete elements.");

  const [query, setQuery] = useState("");
  const [showCostOverTime, setShowCostOverTime] = useState(false);
  // View level: "flat" | "detail" (grouped + elements) | "totals" (group subtotals only).
  const [viewMode, setViewModeRaw] = useState("totals");
  const grouped = viewMode !== "flat";
  const totalsOnly = viewMode === "totals";
  // 2026-03-01 (Dave) — Deleted-elements drawer state.
  const [showDeletedDrawer, setShowDeletedDrawer] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  // The Deleted button only shows when the drawer has something in it.
  useEffect(() => {
    if (!association?.aid || showDeletedDrawer || !writer) return;
    axios.get(`${API}/associations/${association.aid}/elements/deleted`)
      .then((r) => setDeletedCount((r.data || []).length))
      .catch(() => setDeletedCount(0));
  }, [association?.aid, association?.elements, showDeletedDrawer, writer]);
  // Per-group toggles relative to the view default (Categories collapsed, Details expanded).
  const [collapsed, setCollapsed] = useState({});
  const setViewMode = (m) => { setViewModeRaw(m); setCollapsed({}); };
  const [groupSaving, setGroupSaving] = useState(null);
  const [groupDelete, setGroupDelete] = useState(null);
  const [groupDeleteText, setGroupDeleteText] = useState("");
  const [renaming, setRenaming] = useState(null); // { cat, saving }
  // 2026-02-28 (Dave) — default sort is by ID (canonical NNN.NNN),
  // ascending. Matches the reserve-study convention where rows are
  // spoken/read in ID order at board meetings.
  const [sort, setSort] = useState({ key: "element_display_id", dir: "asc" });

  // Pending edits: element_id → { field: value } (only fields that differ).
  const [pendingEdits, setPendingEdits] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const addMenuRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  // 2026-02-16 (Dave) — branded ConfirmDialog replaces window.confirm().
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Element object or null
  // 2026-02-28 (Dave) — Element Attachments modal target.
  const [attachmentsFor, setAttachmentsFor] = useState(null);
  // 2026-03-09 (Dave · Slice 4) — Element History drawer target.
  const [historyFor, setHistoryFor] = useState(null);

  // Slice E3 (2026-02-23) — live-preview header. Every debounced tick we
  // POST /calculate with the current pendingEdits marshalled as in-memory
  // element_overrides. `previewCalc` mirrors the shape of `calc` and
  // becomes the source of truth for the three header numbers whenever
  // pendingEdits is non-empty.
  const [previewCalc, setPreviewCalc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewSeqRef = useRef(0);
  const previewTimerRef = useRef(null);
  // 2026-03-01 (Dave) — Hoist the recalculating state up so ControlsStrip
  // (the blue header row above) can render the "Recalculating…" pill next
  // to Verify Against Proof. Keeps the Elements toolbar layout stable
  // during preview ticks — the pill no longer shoulders the Save button
  // to the right when it appears.
  useEffect(() => {
    onPreviewLoadingChange?.(previewLoading);
    return () => { onPreviewLoadingChange?.(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLoading]);
  // 2026-02-28 (Dave) — Post-save bridge. When Save fires, we fetch the
  // NEW calc ourselves and stash it in `previewCalc` so the header
  // pills render the fresh math instantly instead of momentarily
  // flashing the stale `calc` prop (parent takes ~500-1000 ms to
  // re-fetch and push its new calc down). The ref guards the
  // "empty pendingEdits → clear previewCalc" effect so it doesn't
  // wipe the bridge value. Cleared when the parent's calc prop
  // updates (see the effect near the bottom of this component).
  const postSaveBridgeRef = useRef(false);

  const rawEls = association?.elements || [];
  const studyYear = association?.study_year || 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rawEls;
    return rawEls.filter((e) => {
      const hay = `${e.name || ""} ${e.description || ""} ${e.category || ""} ${e.notes || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rawEls, query]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sort;
    const mult = dir === "asc" ? 1 : -1;

    // Custom per-key extractors so "ID" sorts numerically (100.001 < 358.02
    // rather than lex-order 100 < 2), Description sorts by the description
    // portion (not the numeric ID prefix embedded in `name`), and the new
    // "Next Replacement" column sorts by remaining useful life.
    const sortValue = (el) => {
      if (key === "element_display_id") {
        const id = el.element_display_id || splitNameId(el.name).id || "";
        const n = parseFloat(String(id).replace(/\./g, ""));
        return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
      }
      if (key === "name") {
        // Sort by the descriptive portion only.
        return splitNameId(el.name).desc.toLowerCase();
      }
      if (key === "next_replacement") {
        const rul = el.remaining_useful_life;
        return typeof rul === "number" ? rul : Number.POSITIVE_INFINITY;
      }
      // 2026-02-28 (Dave) — flat-mode Group column: treat empty / null
      // category as "\uFFFF…" so unassigned rows fall to the BOTTOM of
      // the sort, letting the user's assigned group names cluster
      // together at the top.
      // 2026-02-27 (evening, Dave) — Respect pending inline edits so
      // an in-flight category rename re-sorts the row into its new
      // cluster before Save.
      if (key === "category") {
        const pending = pendingEdits[el.element_id] || {};
        const effective = pending.category !== undefined ? pending.category : el.category;
        const c = (effective || "").trim();
        return c ? c.toLowerCase() : "\uFFFF";
      }
      return el[key] ?? "";
    };

    arr.sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * mult;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mult;
    });
    return arr;
  }, [filtered, sort, pendingEdits]);

  const existingCategories = useMemo(() => {
    const s = new Set();
    rawEls.forEach((e) => e.category && s.add(e.category));
    return Array.from(s).sort();
  }, [rawEls]);

  // Effective Include flag with pending-edit overlay (row styling + group include toggles).
  const effIncluded = (e) => {
    const pending = pendingEdits[e.element_id] || {};
    if (pending.included_in_plan !== undefined) return Boolean(pending.included_in_plan);
    return e.included_in_plan !== false;
  };

  // Saved-value totals computed in the browser so they show the instant the tab opens.
  const elTotals = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cost = (e) => (e.included_in_plan === false ? 0 : Number(e.extended_cost) || 0);
    const byCat = {};
    let totalAll = 0;
    let totalFiltered = 0;
    for (const e of rawEls) {
      const c = cost(e);
      totalAll += c;
      if (q && !["name", "description", "category", "notes"].map((k) => String(e[k] ?? "")).join(" ").toLowerCase().includes(q)) continue;
      totalFiltered += c;
      const cat = (e.category || "").trim() || "Uncategorized";
      byCat[cat] = (byCat[cat] || 0) + c;
    }
    return { total_all: totalAll, total_filtered: totalFiltered, by_category: byCat };
  }, [rawEls, query]);
  const catSubtotal = (cat, items) => {
    const byCat = elTotals?.by_category || {};
    if (byCat[cat] !== undefined) return byCat[cat];
    const saved = ((items[0]?.category || "").trim()) || "Uncategorized";
    return byCat[saved] ?? 0;
  };

  const groups = useMemo(() => {
    if (!grouped) return null;
    const m = new Map();
    for (const e of sorted) {
      // 2026-02-27 (evening, Dave) — Reflect inline group-label edits
      // in the group headers. Previously the grouping keyed off the
      // PRISTINE server-side `e.category`, so a row edited from
      // "Roof" → "Building Envelope" would visually stay under the
      // old "Roof" header until Save flushed the change to the
      // server. Prefer the pending edit if present so what the user
      // typed drives the section they see the row under.
      const pending = pendingEdits[e.element_id] || {};
      const effective = pending.category !== undefined ? pending.category : e.category;
      const raw = (effective || "").trim();
      const g = raw || "Uncategorized";
      if (!m.has(g)) m.set(g, []);
      m.get(g).push(e);
    }
    // 2026-02-28 (Dave) — order group headers alphabetically by the
    // Board's assigned names, with `Uncategorized` pinned at the
    // bottom. Previously the header order followed insertion order,
    // which put "Uncategorized" first whenever a low-ID row had no
    // category, and rearranged whenever the user re-sorted the rows.
    const entries = Array.from(m.entries()).sort(([a], [b]) => {
      if (a === "Uncategorized") return 1;
      if (b === "Uncategorized") return -1;
      return a.localeCompare(b, undefined, { sensitivity: "base" });
    });
    return entries.map(([cat, items]) => ({
      cat,
      items,
      subtotal: catSubtotal(cat, items),
    }));
  }, [sorted, grouped, pendingEdits, elTotals]);

  // Slice E4 fix (2026-02-24) — clear any pending edits when the caller
  // switches scenarios (Go-To toggle, delete-active-scenario fallback, etc.)
  // OR when the association's plan changes. Pending edits are compared
  // against the currently-loaded pristine values; those values change when
  // the scope changes, so hanging on to old edits produces wrong deltas
  // and desynced live-preview numbers.
  useEffect(() => {
    setPendingEdits({});
    setPreviewCalc(null);
  }, [activeScenarioId, association?.aid]);

  // Slice E3 (2026-02-23) — the 4 fields that trigger a live preview recalc.
  const TRACKED_FIELDS = ["quantity", "current_cost", "useful_life_when_new", "remaining_useful_life"];

  // Marshal pendingEdits → the ElementOverrideIn[] shape the backend accepts.
  // Only the tracked-4 fields are sent; metadata edits (name/description/notes)
  // don't influence engine math so they'd waste a round-trip.
  const buildOverridesPayload = () => {
    const out = [];
    for (const [element_id, fields] of Object.entries(pendingEdits)) {
      const trackedInThis = TRACKED_FIELDS.filter((f) => fields[f] !== undefined);
      if (trackedInThis.length === 0) continue;
      const entry = { element_id };
      for (const f of trackedInThis) {
        const v = fields[f];
        if (v === "" || v === null || v === undefined) continue;
        const num = Number(v);
        if (!Number.isFinite(num)) continue;
        entry[f] = f === "quantity" || f === "current_cost" ? num : Math.round(num);
      }
      if (Object.keys(entry).length > 1) out.push(entry);
    }
    return out;
  };

  // Debounced live-preview calc. Fires 250 ms after the last edit whenever
  // pendingEdits changes and at least one tracked-4 field is dirty. When
  // pendingEdits empties, previewCalc snaps to null so the header falls
  // back to the base `calc` prop.
  useEffect(() => {
    if (!association?.aid) return undefined;
    const payload = buildOverridesPayload();
    if (payload.length === 0) {
      // 2026-02-28 (Dave) — Skip clearing when the post-save bridge is
      // holding a fresh calc; letting it survive until the parent's
      // calc prop catches up prevents a one-tick fallback to stale
      // header values.
      if (!postSaveBridgeRef.current) {
        setPreviewCalc(null);
        setPreviewLoading(false);
      }
      return undefined;
    }
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(async () => {
      const seq = ++previewSeqRef.current;
      setPreviewLoading(true);
      try {
        const body = {
          variables: currentVariables,
          scenario_id: activeScenarioId || undefined,
          element_overrides: payload,
        };
        const res = await axios.post(
          `${API}/associations/${association.aid}/calculate`,
          body,
        );
        if (seq === previewSeqRef.current) setPreviewCalc(res.data);
      } catch { /* live preview is best-effort */ }
      finally {
        if (seq === previewSeqRef.current) setPreviewLoading(false);
      }
    }, 250);
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [pendingEdits, activeScenarioId, association?.aid, currentVariables]);

  // 2026-02-28 (Dave) — Release the post-save bridge on the FIRST parent
  // `calc` prop update after Save. Once the parent has pushed its fresh
  // calc down, we no longer need to hold the bridge value — the header
  // will fall through to the parent's `calc` on the next render.
  useEffect(() => {
    if (postSaveBridgeRef.current) {
      postSaveBridgeRef.current = false;
      setPreviewCalc(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc]);

  // Dropdown menu for the "+ Element" button. Closes on outside click.
  useEffect(() => {
    if (!showAddMenu) return undefined;
    const onDown = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showAddMenu]);

  // Live Year 1 Budget / Special Assessments shown beside the search box.
  const liveCalc = previewCalc || calc || {};

  if (!association) return <div className="text-slate p-4">Loading…</div>;

  const totalExtended = elTotals?.total_filtered ?? 0;
  const totalAll = elTotals?.total_all ?? 0;

  const onSort = (key) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" },
    );

  const previewActive = !!previewCalc;

  // A cell edit: record only if it differs from the pristine value; otherwise
  // clean it out of the pending map so Undo/Save state stays honest.
  const onEdit = (element_id, field, value) => {
    setError(null);
    setPendingEdits((prev) => {
      const el = rawEls.find((e) => e.element_id === element_id);
      const pristine = normalize(field, el?.[field]);
      const incoming = normalize(field, value);
      const next = { ...prev };
      const forEl = { ...(next[element_id] || {}) };
      if (pristine === incoming) {
        delete forEl[field];
      } else {
        forEl[field] = value;
      }
      if (Object.keys(forEl).length === 0) {
        delete next[element_id];
      } else {
        next[element_id] = forEl;
      }
      return next;
    });
  };

  const dirtyCount = Object.keys(pendingEdits).length;
  const dirtyFields = Object.values(pendingEdits).reduce(
    (n, obj) => n + Object.keys(obj).length,
    0,
  );

  const undoAll = () => {
    setPendingEdits({});
    setError(null);
  };

  const saveAll = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    setError(null);
    try {
      // Normalise numeric fields before shipping.
      const changes = Object.entries(pendingEdits).map(([element_id, fields]) => {
        const clean = {};
        for (const [f, v] of Object.entries(fields)) {
          clean[f] = normalize(f, v);
        }
        return { element_id, fields: clean };
      });
      const res = await axios.patch(
        `${API}/associations/${association.aid}/elements/batch`,
        {
          changes,
          // Snapshot current variables so the auto-created scenario captures
          // the full context (Slice E2).
          variables: currentVariables || undefined,
          // Slice E4b (2026-02-24) — tell the server which scenario the user
          // is currently viewing so delta detection compares against effective
          // (baseline + active override) values, not raw baseline. Enables
          // "revert cell to baseline while on a scenario" to be recognised
          // as a real change and the new scenario to be built as a branch.
          scenario_id: activeScenarioId || undefined,
        },
      );
      // Slice E2 fix (2026-02-20) — response is now { bundle, new_scenario_id, calc }.
      const { bundle, new_scenario_id, calc: freshCalc } = res.data || {};

      // 2026-02-28 (Dave) — Header-pill bridge. The batch endpoint now
      // returns the freshly-computed calc alongside the bundle, so we
      // update the header pills in the SAME roundtrip as PATCH — no
      // second /calculate call, no visible flash of stale numbers.
      // Guard with `postSaveBridgeRef` so the preview effect doesn't
      // wipe the bridge when pendingEdits empties. Cleared when the
      // parent's calc prop catches up (see [calc] effect below).
      if (freshCalc) {
        postSaveBridgeRef.current = true;
        setPreviewCalc(freshCalc);
      }

      // 2026-02-28 (Dave, later) — Row-flash fix. Reconcile parent FIRST
      // (so its `association.elements` carry the fresh values), THEN
      // clear pendingEdits. Reversing the order caused rows to briefly
      // revert to pre-edit values during the ~500 ms parent roundtrip,
      // because cells fell back to stale `raw` the moment pending emptied.
      // Now pending stays visible until raw already matches, so cells
      // render the same value across every intermediate frame.
      if (onBundleUpdated) await onBundleUpdated(bundle, new_scenario_id, freshCalc);
      setPendingEdits({});
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Category Include box saves immediately for every Element in the group.
  const setGroupIncluded = async (g, checked) => {
    const ids = g.items.filter((e) => effIncluded(e) !== checked || e.included_in_plan !== checked).map((e) => e.element_id);
    if (!ids.length) return;
    setGroupSaving(g.cat);
    setError(null);
    try {
      const res = await axios.patch(`${API}/associations/${association.aid}/elements/batch`, {
        changes: ids.map((element_id) => ({ element_id, fields: { included_in_plan: checked } })),
        variables: currentVariables || undefined,
        scenario_id: activeScenarioId || undefined,
      });
      const { bundle, new_scenario_id, calc: freshCalc } = res.data || {};
      if (freshCalc) {
        postSaveBridgeRef.current = true;
        setPreviewCalc(freshCalc);
      }
      if (onBundleUpdated) await onBundleUpdated(bundle, new_scenario_id, freshCalc);
      setPendingEdits((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          if (!next[id] || next[id].included_in_plan === undefined) continue;
          const { included_in_plan, ...rest } = next[id];
          if (Object.keys(rest).length) next[id] = rest;
          else delete next[id];
        }
        return next;
      });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setGroupSaving(null);
    }
  };

  const validateCategoryName = (oldCat, raw) => {
    const v = raw.trim();
    if (!v) return "Name can't be blank";
    if (v.length > 80) return "Keep it under 80 characters";
    if (v === oldCat) return null;
    const taken = (association.elements || []).some((e) => {
      const c = ((e.category || "").trim()) || "Uncategorized";
      return c !== oldCat && c.toLowerCase() === v.toLowerCase();
    });
    if (taken || (v.toLowerCase() === "uncategorized" && oldCat !== "Uncategorized")) return "That category already exists";
    return null;
  };

  const renameCategory = async (oldCat, raw) => {
    const v = raw.trim();
    if (v === oldCat) { setRenaming(null); return; }
    const ids = categoryMembers(oldCat).items.map((e) => e.element_id);
    setRenaming({ cat: oldCat, saving: true });
    try {
      const res = await axios.patch(`${API}/associations/${association.aid}/elements/batch`, {
        changes: ids.map((element_id) => ({ element_id, fields: { category: v } })),
        variables: currentVariables || undefined,
        scenario_id: activeScenarioId || undefined,
      });
      const { bundle, new_scenario_id, calc: freshCalc } = res.data || {};
      if (freshCalc) {
        postSaveBridgeRef.current = true;
        setPreviewCalc(freshCalc);
      }
      if (onBundleUpdated) await onBundleUpdated(bundle, new_scenario_id, freshCalc);
      setPendingEdits((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          if (!next[id] || next[id].category === undefined) continue;
          const { category, ...rest } = next[id];
          if (Object.keys(rest).length) next[id] = rest;
          else delete next[id];
        }
        return next;
      });
      setCollapsed((prev) => (prev[oldCat] === undefined ? prev : { ...prev, [v]: prev[oldCat] }));
      setRenaming(null);
      toast.success(`Renamed to "${v}"`);
    } catch (err) {
      setRenaming({ cat: oldCat, saving: false });
      toast.error(err?.response?.data?.detail || "Rename failed.");
    }
  };

  // Whole category by saved name, ignoring any search filter.
  const categoryMembers = (cat) => ({
    cat,
    items: (association.elements || []).filter(
      (e) => e.is_active !== false && (((e.category || "").trim()) || "Uncategorized") === cat,
    ),
  });

  const confirmGroupDelete = async () => {
    const g = groupDelete;
    const ids = g.items.map((e) => e.element_id);
    const res = await axios.post(`${API}/associations/${association.aid}/elements/bulk-delete`, { element_ids: ids });
    setPendingEdits((prev) => {
      const next = { ...prev };
      for (const id of ids) delete next[id];
      return next;
    });
    setGroupDelete(null);
    if (onBundleUpdated) await onBundleUpdated(res.data);
    toast(`Category deleted — "${g.cat}" (${ids.length} Element${ids.length === 1 ? "" : "s"})`, {
      description: "You have 10 seconds to undo.",
      duration: 10000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const r = await axios.post(`${API}/associations/${association.aid}/elements/bulk-restore`, { element_ids: ids });
            if (onBundleUpdated) await onBundleUpdated(r.data);
            toast.success(`"${g.cat}" restored`);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Restore failed.");
          }
        },
      },
    });
  };

  const doDelete = (el) => {
    setDeleteConfirm(el);
  };

  const confirmSoftDelete = async () => {
    const el = deleteConfirm;
    if (!el) return;
    setDeletingId(el.element_id);
    setError(null);
    try {
      const res = await axios.delete(`${API}/elements/${el.element_id}`);
      // Also clear any pending edits on that row.
      setPendingEdits((prev) => {
        const next = { ...prev };
        delete next[el.element_id];
        return next;
      });
      setDeleteConfirm(null);
      if (onBundleUpdated) await onBundleUpdated(res.data);
      // 2026-03-01 (Dave) — Undo toast. 10-second window to reverse an
      // accidental soft-delete without hunting for the "Deleted elements"
      // drawer. Restore route is idempotent so double-tap is safe.
      toast(`Element deleted — "${el.name}"`, {
        description: "You have 10 seconds to undo.",
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              const restored = await axios.post(`${API}/elements/${el.element_id}/restore`);
              if (onBundleUpdated) await onBundleUpdated(restored.data);
              toast.success(`"${el.name}" restored`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Restore failed.");
            }
          },
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      setError(msg);
      // Re-throw so ConfirmDialog surfaces the same error in its coral
      // banner and keeps the dialog open for the user to retry / cancel.
      throw new Error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const onCreated = async (bundle) => {
    if (onBundleUpdated) await onBundleUpdated(bundle);
  };

  // Column-count math for group/subtotal spanning.
  // Order (grouped=true, 13 cols):
  //   1 ID · 2 Description · 3 Useful Life · 4 Remaining Life · 5 Quantity
  //   6 UOM · 7 Cost/UOM · 8 Extended Cost · 9 Next Repl. · 10 Last Insp.
  //   11 Placed In Svc. · 12 Include · 13 Attachments (paperclip+trash cluster)
  // Flat mode inserts "Group" as col 3, pushing Extended to col 9 (14 cols).
  // 2026-03-01 (Dave) — The old separate trailing "Actions" column was
  // folded into the (renamed) Attachments column, so writer status no
  // longer changes the count.
  const preExtendedCols = grouped ? 7 : 8;   // columns before Extended Cost
  const colCount = grouped ? 13 : 14;
  const totalCols = colCount;
  const postExtendedCols = totalCols - preExtendedCols - 1;

  return (
    <div
      className="panel-compact flex flex-col flex-1 overflow-hidden"
      data-testid="screen-elements"
    >
      <div
        className="grid grid-cols-[1fr_auto_1fr] items-center mb-1.5 pb-1 border-b-2 gap-3"
        style={{ borderColor: "#EA6A47" }}
      >
        <div className="min-w-0 flex items-center gap-4">
        <div className="shrink-0">
          <div className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy leading-none">
            Element Inventory ({filtered.length})
          </div>
          <div className="text-[10.5px] text-slate font-mono tabular-nums leading-tight mt-0.5">
            {/* 2026-02-23 (Dave) — element count moved into the title
                parentheses; "Extended Cost" now leads this line. */}
            <button
              type="button"
              onClick={() => setShowCostOverTime(true)}
              className="inline-flex items-center gap-0.5 hover:text-navy transition-colors"
              title="View each element's inflated cost across the next 30 years"
              data-testid="elements-cost-over-time-btn"
            >
              Extended Cost
              <ChevronRight size={11} className="shrink-0" />
            </button>{" "}
            <span className="text-ink font-semibold" data-testid="elements-extended-total">{fmtCurrency(totalExtended)}</span>
            {query && filtered.length !== rawEls.length && (
              <>
                {" "}· filtered from{" "}
                <span className="text-ink font-semibold">{fmtCurrency(totalAll)}</span>
              </>
            )}
          </div>
        </div>

          <div
          data-testid="view-mode-switch"
          style={{ marginLeft: 72 }}
          className="flex items-center border border-rule bg-white rounded-[3px] overflow-hidden text-[11px] shrink-0"
        >
          {[
            ["totals", "Categories", "Category totals; expand any category for detail"],
            ["detail", "Details", "Categories with element detail"],
            ["flat", "Flat", "Flat list of all elements"],
          ].map(([mode, label, title]) => (
            <button
              key={mode}
              data-testid={`view-mode-${mode}`}
              onClick={() => setViewMode(mode)}
              title={title}
              className={`px-1.5 py-1 transition-colors ${mode === "totals" ? "" : "border-l border-rule"} ${
                viewMode === mode ? "bg-[var(--u-navy-wash)] text-navy font-semibold" : "text-slate hover:text-navy"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {/* Edit indicator sits right of the switch so the switch never moves. */}
        {writer && dirtyCount > 0 && (
          <span className="text-coral font-semibold text-[11px] leading-tight whitespace-nowrap font-mono flex flex-col" data-testid="dirty-summary">
            <span>{`${dirtyFields} edit${dirtyFields === 1 ? "" : "s"}`}</span>
            <span>{`across ${dirtyCount} element${dirtyCount === 1 ? "" : "s"}`}</span>
          </span>
        )}
        </div>

        {/* Search centered; live figures + write actions to its right. */}
        <div className="flex items-center gap-1.5" data-testid="elements-toolbar-center">
          <div className="flex items-center border border-rule bg-white px-1.5 py-1 rounded-[3px]" style={{ marginLeft: -70 }}>
            <Search size={12} className="text-slate mr-1" />
            <input
              data-testid="elements-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="outline-none text-[12px] w-[210px] bg-transparent"
            />
          </div>
        </div>

        <div className="flex items-center gap-1.5 min-w-0" style={{ marginRight: "calc(10.5px - var(--u-panel-pad-x))" }} data-testid="elements-toolbar-right">
          <div className="flex-1 flex items-center gap-4 min-w-0" data-testid="elements-live-stats">
            <LiveStat label="Year 1 Budget" value={liveCalc.year1_contribution} dirty={!!previewCalc} loading={previewLoading} testid="live-y1a" />
            <div className="ml-auto font-mono text-[12px]" style={{ marginRight: "calc(16px + 5ch)" }}>
              <LiveStat label="Special Assessments" value={liveCalc.special_assessments_total} dirty={!!previewCalc} testid="live-sa" />
            </div>
          </div>
          {writer && (
            <>
              {deletedCount > 0 && (
              <button
                onClick={() => setShowDeletedDrawer(true)}
                data-testid="btn-deleted-elements"
                className="flex items-center gap-1 border border-rule bg-white text-slate hover:border-navy hover:text-navy px-1.5 py-1 text-[11px] font-semibold rounded-[3px]"
                title="Open the Deleted Elements drawer to restore a soft-deleted row"
              >
                <ArchiveRestore size={12} /> Deleted
              </button>
              )}
              {dirtyCount > 0 && (
              <button
                onClick={undoAll}
                disabled={saving}
                data-testid="elements-undo"
                className="flex items-center gap-0.5 border border-rule bg-white text-navy hover:border-navy px-1 py-1 text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed rounded-[3px]"
                title="Revert all pending element edits"
              >
                <Undo2 size={11} /> Undo
              </button>
              )}
              {/* Slice E5q (2026-02-09) — swapped order: + Element now sits
                  BEFORE Save so Save is the rightmost action (primary
                  visual anchor for edit sessions). */}
              <div className="relative" ref={addMenuRef}>
                <button
                  data-testid="btn-add-element"
                  data-write-action="true"
                  onClick={() => setShowAddMenu((v) => !v)}
                  className="flex items-center gap-0.5 border border-navy bg-white text-navy hover:bg-navy hover:text-white px-1 py-1 text-[11px] font-semibold rounded-[3px]"
                  title="Add or import elements"
                  aria-haspopup="menu"
                  aria-expanded={showAddMenu}
                >
                  <Plus size={11} /> Add Element(s)
                  <ChevronDown size={11} />
                </button>
                {showAddMenu && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-1 border border-rule bg-white shadow-lg z-50"
                    style={{ minWidth: 200 }}
                    data-testid="add-element-menu"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setShowAddMenu(false);
                        setShowAdd(true);
                      }}
                      className="w-full text-left px-3 py-1.5 text-[11.5px] text-navy hover:bg-navy-wash"
                      data-testid="menu-add-element"
                    >
                      Add Element
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setShowAddMenu(false);
                        setShowImport(true);
                      }}
                      className="w-full text-left px-3 py-1.5 text-[11.5px] text-navy hover:bg-navy-wash border-t border-rule"
                      data-testid="menu-import-elements"
                    >
                      Import Elements
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={saveAll}
                disabled={dirtyCount === 0 || saving}
                data-testid="elements-save"
                data-write-action="true"
                className="flex items-center gap-1 bg-navy text-white hover:bg-[#12406B] px-2 py-1 text-[11px] font-semibold rounded-[3px] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ boxShadow: dirtyCount > 0 && !saving ? "0 0 0 2px #EA6A47" : "none" }}
                title="Commit all pending element edits"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div
          className="text-[11.5px] text-coral bg-coral-wash border border-coral px-3 py-1 mb-1.5"
          data-testid="elements-error"
        >
          {error}
        </div>
      )}

      <div className="overflow-auto flex-1">
        <table className="u-table u-table-dense" data-testid="elements-table" style={{ width: "100%", tableLayout: "fixed" }}>
          {/* Column widths — Description gets the flex, life/qty/UoM narrowed.
              2026-02-09 (Dave): Quantity/UoM/Cost/Extended trimmed to
              reclaim ~46 px for the Description column. */}
          <colgroup>{[
            <col key="id" style={{ width: 106 }} />,
            <col key="desc" />,
            !grouped && <col key="group" style={{ width: 124 }} />,
            <col key="ul" style={{ width: 62 }} />,
            <col key="rl" style={{ width: 78 }} />,
            <col key="qty" style={{ width: 84 }} />,
            <col key="uom" style={{ width: 66 }} />,
            <col key="cost" style={{ width: 84 }} />,
            <col key="ext" style={{ width: 96 }} />,
            <col key="next" style={{ width: 82 }} />,
            <col key="last" style={{ width: 82 }} />,
            <col key="placed" style={{ width: 82 }} />,
            <col key="inc" style={{ width: 62 }} />,
            <col key="attach" style={{ width: 110 }} />,
          ]}</colgroup>
          <thead>
            <tr>
              {/* 1. ID — sortable per Dave 2026-02-09. Header scooted right
                  ~1 character (18 px) after Dave's 2026-02-09 "left 1 char"
                  request. ID column now uses a numeric comparator so
                  100.001 < 358.02 (not lex-order). */}
              <SortableTh top={<>&nbsp;</>} bottom="ID" sortKey="element_display_id" align="left" padLeft={18} sort={sort} onSort={onSort} />

              {/* 2. Description (was Name) — editable text. Left-aligned
                  header scooted right (12 px) after Dave's "left 1 char" pass.
                  Sort comparator now uses the descriptive portion only, not
                  the "358.02 " numeric prefix embedded in `name`. */}
              <SortableTh top={<>&nbsp;</>} bottom="Description" sortKey="name" align="left" padLeft={12} sort={sort} onSort={onSort} />

              {!grouped && (
                <SortableTh top={<>&nbsp;</>} bottom="Group" sortKey="category" align="left" padLeft={12} sort={sort} onSort={onSort} />
              )}

              {/* 3. Useful Life */}
              <SortableTh
                top="Useful"
                bottom="Life"
                sortKey="useful_life_when_new"
                align="right"
                // 2026-02-23 (Dave) — data cells' <input> has 3 px inner
                // right-padding, so numbers render 3 px left of the
                // default right-aligned header. Bump header paddingRight
                // to keep the label visually centered over the digits.
                padRight={15}
                sort={sort}
                onSort={onSort}
              />

              {/* 4. Remaining Life */}
              <SortableTh
                top="Remaining"
                bottom="Life"
                sortKey="remaining_useful_life"
                align="right"
                padRight={15}
                sort={sort}
                onSort={onSort}
              />

              {/* 5. Quantity — spelled out + centered per Dave 2026-02-28 */}
              <SortableTh
                top={<>&nbsp;</>}
                bottom="Quantity"
                sortKey="quantity"
                align="center"
                sort={sort}
                onSort={onSort}
              />

              {/* 6. Unit of Measure */}
              <PlainTh top="Unit of" bottom="Measure" align="right" />

              {/* 7. Cost / UoM */}
              <SortableTh
                top="Cost /"
                bottom="UoM"
                sortKey="current_cost"
                align="right"
                sort={sort}
                onSort={onSort}
              />

              {/* 8. Extended Cost */}
              <SortableTh
                top="Extended"
                bottom="Cost"
                sortKey="extended_cost"
                align="right"
                sort={sort}
                onSort={onSort}
              />

              {/* 9. Next Replacement — sortable per Dave 2026-02-09.
                  Uses `next_replacement` sortKey (extractor reads
                  remaining_useful_life since studyYear is constant across
                  rows). */}
              <SortableTh
                top="Next"
                bottom="Replacement"
                sortKey="next_replacement"
                align="right"
                sort={sort}
                onSort={onSort}
              />

              {/* 10. Last Inspection */}
              <PlainTh top="Last" bottom="Inspection" align="right" />

              {/* 11. Placed In Service */}
              <PlainTh top="Placed In" bottom="Service" align="right" />

              {/* 11.5 Include (2026-02-28 Dave) — checkbox column */}
              <PlainTh top={<>&nbsp;</>} bottom="Include" align="center" />

              {/* 12. Attachments (was Notes, repurposed 2026-03-01 Dave) —
                  header left-aligned to match the cell content. */}
              <PlainTh top={<>&nbsp;</>} bottom="Attachments" align="left" padLeft={4} />
            </tr>
          </thead>
          <tbody>
            {grouped
              ? groups.map((g) => {
                  const isCollapsed = totalsOnly !== !!collapsed[g.cat];
                  // Details view: an open category shows its total in a footer row instead of the header.
                  const totalAtBottom = !totalsOnly && !isCollapsed;
                  return (
                    <React.Fragment key={g.cat}>
                      <tr
                        data-testid={`group-${g.cat}`}
                        onClick={() => setCollapsed((prev) => ({ ...prev, [g.cat]: !prev[g.cat] }))}
                        style={{
                          background: "var(--u-navy-wash)",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        <td colSpan={preExtendedCols} className="text-[11px] uppercase tracking-wider text-navy">
                          <span className="inline-flex items-center gap-1">
                            <span data-testid={`group-toggle-${g.cat}`} aria-label={isCollapsed ? "Expand" : "Collapse"}>
                              {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                            </span>
                            {renaming?.cat === g.cat ? (
                              <CategoryRenameInput
                                current={g.cat}
                                saving={renaming.saving}
                                validate={(v) => validateCategoryName(g.cat, v)}
                                onSubmit={(v) => renameCategory(g.cat, v)}
                                onCancel={() => setRenaming(null)}
                              />
                            ) : (
                              <>
                                {g.cat} <span className="text-slate">({g.items.length})</span>
                                {writer && (
                                  <button
                                    type="button"
                                    onClick={(ev) => { ev.stopPropagation(); setRenaming({ cat: g.cat, saving: false }); }}
                                    title="Rename this category"
                                    aria-label="Rename this category"
                                    data-testid={`group-rename-${g.cat}`}
                                    className="ml-1 text-slate hover:text-sky transition-colors p-0.5"
                                  >
                                    <Pencil size={11} />
                                  </button>
                                )}
                              </>
                            )}
                          </span>
                        </td>
                        <td className="num text-navy">{totalAtBottom ? "" : fmtCurrency(g.subtotal)}</td>
                        <td colSpan={postExtendedCols - 2}></td>
                        <td className="text-center" onClick={(ev) => ev.stopPropagation()}>
                          {groupSaving === g.cat ? (
                            <Loader2 size={12} className="animate-spin text-slate inline" data-testid={`group-include-saving-${g.cat}`} />
                          ) : (
                          <GroupIncludeBox
                            items={g.items}
                            isIncluded={effIncluded}
                            disabled={!writer || groupSaving !== null}
                            onChange={(checked) => setGroupIncluded(g, checked)}
                            testId={`group-include-${g.cat}`}
                          />
                          )}
                        </td>
                        <td onClick={(ev) => ev.stopPropagation()} style={{ paddingLeft: 6 }}>
                          {writer && (
                            <button
                              type="button"
                              onClick={() => { setGroupDeleteText(""); setGroupDelete(categoryMembers(g.cat)); }}
                              title={`Delete every Element in ${g.cat}`}
                              data-testid={`group-delete-${g.cat}`}
                              className="text-slate hover:text-coral transition-colors p-0.5"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                      {!isCollapsed &&
                        g.items.map((e) => (
                          <ElementRow
                            key={e.element_id}
                            e={e}
                            studyYear={studyYear}
                            grouped={grouped}
                            pendingEdits={pendingEdits}
                            onEdit={onEdit}
                            onDelete={doDelete}
                            onOpenAttachments={setAttachmentsFor}
                            onOpenHistory={setHistoryFor}
                            readOnly={!writer}
                            deleting={deletingId === e.element_id}
                            canDeleteRow={canDeleteElement(e)}
                            cannotDeleteReason={deleteLockReason(e)}
                          />
                        ))}
                      {totalAtBottom && (
                        <tr data-testid={`group-total-${g.cat}`} style={{ borderTop: "1px solid #1C4E80" }}>
                          <td colSpan={preExtendedCols} className="text-right text-[10.5px] uppercase tracking-wider font-semibold text-navy" style={{ paddingRight: 8 }}>
                            Total {g.cat}
                          </td>
                          <td className="num text-navy font-semibold">{fmtCurrency(g.subtotal)}</td>
                          <td colSpan={postExtendedCols}></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              : sorted.map((e) => (
                  <ElementRow
                    key={e.element_id}
                    e={e}
                    studyYear={studyYear}
                    grouped={grouped}
                    pendingEdits={pendingEdits}
                    onEdit={onEdit}
                    onDelete={doDelete}
                    onOpenAttachments={setAttachmentsFor}
                    onOpenHistory={setHistoryFor}
                    readOnly={!writer}
                    deleting={deletingId === e.element_id}
                    canDeleteRow={canDeleteElement(e)}
                    cannotDeleteReason={deleteLockReason(e)}
                  />
                ))}
            <tr
              style={{ background: "var(--u-navy-wash)", fontWeight: 600 }}
              data-testid="elements-total-row"
            >
              <td colSpan={preExtendedCols} className="text-right pr-3">
                TOTAL
              </td>
              <td className="num">{fmtCurrency(totalExtended)}</td>
              <td colSpan={postExtendedCols}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddElementModal
          aid={association.aid}
          associationDisplayId={association.tenant_display_id}
          existingCategories={existingCategories}
          onCreated={onCreated}
          onClose={() => setShowAdd(false)}
        />
      )}

      {showImport && (
        <ImportElementsModal
          aid={association.aid}
          onImported={onCreated}
          onClose={() => setShowImport(false)}
        />
      )}

      {/* 2026-02-28 (Dave) — Element Attachments modal. `onChanged`
          refetches the association bundle so the paperclip badge
          (attachment_count) updates in place. */}
      {attachmentsFor && (
        <AttachmentsModal
          element={attachmentsFor}
          canWrite={writer}
          onClose={() => setAttachmentsFor(null)}
          onChanged={async () => {
            // 2026-03-01 (Dave) — Invalidate the hover-thumbnail cache
            // for this element so the next hover refetches the fresh list.
            invalidateAttachmentHoverCache("element", attachmentsFor.element_id);
            try {
              const r = await axios.get(`${API}/associations/${association.aid}`);
              if (onBundleUpdated) await onBundleUpdated(r.data);
            } catch (_e) { /* keep modal open — count refresh best-effort */ }
          }}
        />
      )}

      {/* 2026-02-16 (Dave) — branded replacement for window.confirm() on
          the Elements soft-delete flow. */}
      <ConfirmDialog
        open={!!deleteConfirm}
        title="Soft-delete this element?"
        message={
          deleteConfirm ? (
            <>
              <span className="font-semibold">&ldquo;{deleteConfirm.name}&rdquo;</span>{" "}
              will be hidden from the inventory. You'll get a 10-second
              Undo toast, and it stays available in the Deleted Elements
              drawer.
            </>
          ) : ""
        }
        confirmLabel="Delete"
        tone="danger"
        onConfirm={confirmSoftDelete}
        onCancel={() => setDeleteConfirm(null)}
        testid="element-delete-confirm"
      />

      <ConfirmDialog
        open={!!groupDelete}
        title="Delete this entire category?"
        message={
          groupDelete ? (
            <>
              {groupDelete.items.length === 1 ? "The only Element" : (
                <>All <span className="font-semibold">{groupDelete.items.length}</span> Elements</>
              )}{" "}in{" "}
              <span className="font-semibold">&ldquo;{groupDelete.cat}&rdquo;</span> (
              {fmtCurrency(catSubtotal(groupDelete.cat, groupDelete.items))} included) will be hidden
              from the inventory. You'll get a 10-second Undo, and the whole category can be restored
              from the Deleted Elements drawer.
              <div className="mt-3 text-[12px] text-slate">
                Type <span className="font-semibold text-navy">{groupDelete.cat}</span> to confirm:
              </div>
              <input
                autoFocus
                value={groupDeleteText}
                onChange={(ev) => setGroupDeleteText(ev.target.value)}
                className="mt-1 w-full border border-rule rounded-[3px] px-2 py-1 text-[13px]"
                data-testid="group-delete-confirm-input"
              />
            </>
          ) : ""
        }
        confirmLabel="Delete Category"
        tone="danger"
        confirmDisabled={!groupDelete || groupDeleteText.trim() !== groupDelete.cat}
        onConfirm={confirmGroupDelete}
        onCancel={() => setGroupDelete(null)}
        testid="group-delete-confirm"
      />

      {showCostOverTime && (
        <ElementCostOverTimeModal
          association={association}
          variables={currentVariables}
          onClose={() => setShowCostOverTime(false)}
        />
      )}

      {showDeletedDrawer && (
        <DeletedElementsDrawer
          aid={association.aid}
          onClose={() => setShowDeletedDrawer(false)}
          onChanged={onBundleUpdated}
        />
      )}

      {/* 2026-03-09 (Dave · Slice 4) — Element History drawer.
          Right-side slide-out, admin + analyst only. Reads
          /associations/{aid}/elements/{eid}/history. */}
      {historyFor && (
        <ElementHistoryDrawer
          aid={association.aid}
          element={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}
